import React, { useState } from 'react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { IconButton } from '@/design-system/icon-button';
import { Hub } from '@/services/api/hubs';
import { Zone } from '@/services/api/zones';

interface ZonesFloatingPanelProps {
  hub: Hub;
  zones: Zone[];
  /** Live cell count per zone id (loaded cells, not the API's stale field). */
  cellCounts: Record<string, number>;
  /** Roster size per zone id. The entry point for cell-less zones, which
   * get no map badge. */
  driverCounts: Record<string, number>;
  activeZoneId: string | null;
  isLoading: boolean;
  /**
   * Whether this user may create / edit / delete zones. Managing a zone's
   * mitra stays available either way — see `config/zone-permissions`.
   */
  canEditZones: boolean;
  onSelectZone: (zoneId: string) => void;
  onManageDrivers: (zoneId: string) => void;
  onNewZone: () => void;
  onEditZone: (zone: Zone) => void;
  onDeleteZone: (zone: Zone) => void;
}

/**
 * Right-edge floating zones list. Collapsible to a thin pill so the map
 * gets full width when ops is just looking at coverage rather than
 * actively editing.
 */
const ZonesFloatingPanel: React.FC<ZonesFloatingPanelProps> = ({
  hub,
  zones,
  cellCounts,
  driverCounts,
  activeZoneId,
  isLoading,
  canEditZones,
  onSelectZone,
  onManageDrivers,
  onNewZone,
  onEditZone,
  onDeleteZone,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const totalCells = zones.reduce(
    (sum, z) => sum + (cellCounts[z.id] ?? z.cellCount),
    0
  );
  const uncoveredZones = zones.filter(
    (z) => (driverCounts[z.id] ?? 0) === 0
  ).length;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="flex h-32 w-10 flex-col items-center justify-center gap-2 rounded-2xl border border-stroke-soft-200 bg-bg-white-0 px-2 py-3 shadow-sm hover:bg-bg-weak-50"
        title="Expand zones panel"
      >
        <i className="ri-arrow-left-s-line text-[16px] text-text-sub-600" />
        <span
          className="label-eyebrow"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          Zones · {zones.length}
        </span>
      </button>
    );
  }

  return (
    <aside className="flex w-[300px] flex-col overflow-hidden rounded-2xl border border-stroke-soft-200 bg-bg-white-0 shadow-md">
      <div className="flex items-center justify-between border-b border-stroke-soft-200 px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="label-eyebrow">Zones</p>
          <p className="truncate font-mono text-[10px] tabular text-text-soft-400">
            {hub.code}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {canEditZones && (
            <Button
              size="xs"
              onClick={onNewZone}
              className="h-6 px-2 text-[11px]"
            >
              <i className="ri-add-line mr-0.5 text-[12px]" />
              New
            </Button>
          )}
          <IconButton
            type="button"
            tone="neutral"
            style="ghost"
            size="xs"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse panel"
            title="Collapse panel"
          >
            <i className="ri-arrow-right-s-line" />
          </IconButton>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <p className="px-3 py-2 text-[12px] text-text-sub-600">
            Loading zones…
          </p>
        )}
        {!isLoading && zones.length === 0 && (
          <div className="px-3 py-4 text-center text-[12px] text-text-sub-600">
            <p>No zones yet for this hub.</p>
            {canEditZones && (
              <button
                type="button"
                onClick={onNewZone}
                className="mt-2 font-semibold tracking-tight text-accent hover:underline"
              >
                Create the first zone →
              </button>
            )}
          </div>
        )}
        {zones.map((z) => {
          const isActive = z.id === activeZoneId;
          return (
            <button
              key={z.id}
              type="button"
              onClick={() => onSelectZone(z.id)}
              className={`block w-full border-b border-l-2 border-stroke-soft-200 px-3 py-2.5 text-left transition-colors last:border-b-0 ${
                isActive
                  ? 'border-l-accent bg-bg-weak-50'
                  : 'border-l-transparent hover:bg-bg-weak-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className="h-3 w-3 shrink-0 rounded-full border border-stroke-soft-200"
                  style={{ backgroundColor: z.color }}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12.5px] font-semibold tracking-tight text-text-strong-950">
                    {z.code}
                  </p>
                  <p className="truncate text-[11px] text-text-sub-600">
                    {z.name}
                  </p>
                </div>
                <span
                  className={`inline-flex shrink-0 items-center gap-0.5 font-mono text-[10.5px] font-semibold tabular-nums ${
                    (driverCounts[z.id] ?? 0) === 0
                      ? 'text-warning-base'
                      : 'text-text-sub-600'
                  }`}
                  title={`${driverCounts[z.id] ?? 0} mitra on this zone`}
                >
                  <i className="ri-user-fill text-[12px]" aria-hidden="true" />
                  {driverCounts[z.id] ?? 0}
                </span>
                <Badge
                  status={z.status === 'active' ? 'success' : 'neutral'}
                  size="sm"
                  className="tabular-nums"
                >
                  {cellCounts[z.id] ?? z.cellCount}
                </Badge>
              </div>

              {/* Two labelled sections, not one action strip: the mitra list
                  and the zone record are different objects, so a "Mitra"
                  action sitting next to Edit/Delete read like a third way to
                  change the zone itself. */}
              {isActive && (
                <div className="mt-2.5 border-t border-stroke-soft-200 pt-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="label-eyebrow">Zone</span>
                    {canEditZones ? (
                      <div className="flex items-center gap-1">
                        <Button
                          tone="primary"
                          style="link"
                          size="xs"
                          className="py-0 text-[11px] font-semibold"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditZone(z);
                          }}
                        >
                          Edit
                        </Button>
                        <span className="text-text-soft-400">·</span>
                        <Button
                          tone="destructive"
                          style="link"
                          size="xs"
                          className="py-0 text-[11px] font-semibold"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteZone(z);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    ) : (
                      // Says why the actions are gone, so a read-only user
                      // isn't left wondering whether the UI is broken.
                      <span
                        className="inline-flex items-center gap-1 text-[11px] text-text-soft-400"
                        title="Only authorised accounts can add, edit, or delete zones and their coverage."
                      >
                        <i className="ri-lock-line text-[12px]" aria-hidden="true" />
                        Read-only
                      </span>
                    )}
                  </div>

                  <div className="mt-2 flex items-center justify-between gap-2 border-t border-stroke-soft-200 pt-2">
                    <span className="label-eyebrow">Mitra</span>
                    <Button
                      tone="primary"
                      style="stroke"
                      size="xs"
                      className="h-6 px-2 text-[11px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        onManageDrivers(z.id);
                      }}
                    >
                      <i
                        className="ri-user-line mr-1 text-[12px]"
                        aria-hidden="true"
                      />
                      {(driverCounts[z.id] ?? 0) === 0
                        ? 'Assign mitra'
                        : 'Manage mitra'}
                    </Button>
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {!isLoading && zones.length > 0 && (
        <div className="border-t border-stroke-soft-200 bg-bg-weak-50/60 px-3 py-2 text-[11px]">
          <div className="flex items-center justify-between">
            <span className="text-text-sub-600">Hub total</span>
            <span className="font-mono font-semibold tabular text-text-strong-950">
              {totalCells} {totalCells === 1 ? 'cell' : 'cells'}
            </span>
          </div>
          {uncoveredZones > 0 && (
            <div className="mt-1 flex items-center justify-between">
              <span className="text-text-sub-600">Without mitra</span>
              <span className="font-mono font-semibold tabular text-warning-base">
                {uncoveredZones} {uncoveredZones === 1 ? 'zone' : 'zones'}
              </span>
            </div>
          )}
        </div>
      )}
    </aside>
  );
};

export default ZonesFloatingPanel;
